import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { AirplaneTilt, Bed, Stamp, ShieldCheck, MapTrifold, ArrowUpRight, Plus, Minus } from "@phosphor-icons/react";
import Newsletter from "@/components/Newsletter";
import { waLink } from "@/lib/site";

const SERVICES = [
    {
        icon: AirplaneTilt,
        title: "Flight Booking",
        desc: "Economy to private jets. We partner with 200+ airlines and negotiate quiet rates you won't find on search engines.",
        points: ["Round trip & multi-city", "Business & First class deals", "Frequent flyer miles managed"],
        detail: "Our flight specialists monitor fare trends daily and have access to consolidator rates unavailable to the public. Whether you need a last-minute business class upgrade or a complex multi-city itinerary across six countries, we handle every segment.",
    },
    {
        icon: Bed,
        title: "Hotel Booking",
        desc: "From 5★ resorts to boutique ryokans and private villas. We have stayed in every hotel we recommend.",
        points: ["Room category upgrades", "Early check-in / late check-out", "Amenities on arrival"],
        detail: "We are preferred partners with over 300 properties worldwide. This means complimentary upgrades, welcome amenities, and priority check-in that you simply cannot get by booking direct or through an OTA.",
    },
    {
        icon: Stamp,
        title: "Visa Assistance",
        desc: "End-to-end visa processing with a dedicated officer per application. High approval rate across 40+ countries.",
        points: ["Document checklist & review", "Appointment scheduling", "Status tracking"],
        detail: "Our visa team has processed thousands of applications across 40+ countries. We review every document before submission, schedule appointments at the right consulates, and track your application status in real time.",
    },
    {
        icon: ShieldCheck,
        title: "Travel Insurance",
        desc: "Comprehensive coverage with trusted underwriters — medical, cancellation, baggage, and adventure sports add-ons.",
        points: ["Global medical emergency cover", "Trip cancellation protection", "24/7 claims concierge"],
        detail: "We partner with A-rated underwriters to offer policies that actually pay out. Our 24/7 claims concierge means you have a real person to call at 2am in a foreign hospital — not a chatbot.",
    },
    {
        icon: MapTrifold,
        title: "Customized Tours",
        desc: "Your trip, your pace. We design private itineraries around your interests — art, food, adventure, wellness.",
        points: ["Private guides & transport", "Flexible day-by-day pacing", "Hidden experiences"],
        detail: "Every custom tour starts with a conversation. We learn your travel style, interests, and pace — then build a day-by-day itinerary with private guides, curated restaurants, and experiences that aren't in any guidebook.",
    },
];

const FAQS = [
    { q: "How far in advance should I book?", a: "For peak season travel (Dec–Jan, summer), we recommend 3–4 months ahead. For off-peak, 6–8 weeks is usually sufficient. That said, we've pulled off incredible last-minute trips — just ask." },
    { q: "Do you charge a planning fee?", a: "Our planning service is complimentary for packages above ₹1,00,000. For smaller bookings or standalone visa assistance, a nominal service fee applies — always disclosed upfront." },
    { q: "Can I modify my itinerary after booking?", a: "Yes. We build flexibility into every itinerary. Minor changes are usually free; significant changes depend on supplier policies, which we'll explain clearly before you commit." },
    { q: "What happens if something goes wrong during my trip?", a: "You call us. Our concierge is available 24/7 for travelers currently on a trip with us. We've handled missed flights, medical emergencies, and natural disasters — always with calm and speed." },
    { q: "Do you handle group travel?", a: "Absolutely. Groups of 6 to 60+ are a specialty. We manage logistics, negotiate group rates, and assign a dedicated coordinator for the duration of your trip." },
];

function FAQItem({ item, isOpen, onToggle }) {
    return (
        <div className="border-b border-white/10">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between py-6 text-left group"
            >
                <span className="font-serif text-lg lg:text-xl group-hover:text-gold transition-colors">{item.q}</span>
                {isOpen ? (
                    <Minus size={18} weight="light" className="text-gold shrink-0 ml-4" />
                ) : (
                    <Plus size={18} weight="light" className="text-white/50 group-hover:text-gold shrink-0 ml-4 transition-colors" />
                )}
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <p className="font-sans text-sm text-white/65 leading-relaxed pb-6">{item.a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function Services() {
    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div data-testid="page-services" className="pt-28">
            {/* Hero */}
            <section className="py-20 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <p className="overline">What we do</p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight max-w-4xl">
                        A full service <em className="italic text-gold">travel atelier.</em>
                    </h1>
                    <p className="font-sans text-base text-white/65 mt-6 max-w-2xl leading-relaxed">
                        Five services, one promise — you speak to a real human, and they own your trip from start to finish.
                    </p>
                </div>
            </section>

            {/* Services grid */}
            <section className="pb-24 lg:pb-32 bg-navy" data-testid="services-list">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
                        {SERVICES.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.article
                                    key={s.title}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.6, delay: i * 0.06 }}
                                    className="p-8 lg:p-10 border-r border-b border-white/10 group hover:bg-white/[0.02] transition-colors duration-500"
                                    data-testid={`service-item-${i}`}
                                >
                                    <Icon size={40} weight="light" className="text-gold" />
                                    <h3 className="font-serif text-2xl lg:text-3xl mt-6">{s.title}</h3>
                                    <p className="font-sans text-sm text-white/65 mt-4 leading-relaxed">{s.desc}</p>
                                    <ul className="mt-6 space-y-2">
                                        {s.points.map((p) => (
                                            <li key={p} className="font-sans text-sm text-white/55">— {p}</li>
                                        ))}
                                    </ul>
                                    <p className="font-sans text-sm text-white/45 mt-5 leading-relaxed border-t border-white/10 pt-5">{s.detail}</p>
                                    <div className="mt-8 flex gap-3">
                                        <a
                                            href={waLink(`Hi! I'd like to know more about your ${s.title} service.`)}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`service-cta-${i}`}
                                            className="inline-flex items-center gap-1.5 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                                        >
                                            Inquire <ArrowUpRight size={12} weight="bold" />
                                        </a>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>

                    <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-10 border border-white/10">
                        <div>
                            <p className="overline">Unsure where to start?</p>
                            <h3 className="font-serif text-2xl sm:text-3xl mt-2">Tell us how you want to feel. We'll design the rest.</h3>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact"
                            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-3.5 font-sans font-medium uppercase tracking-wider text-xs transition-colors whitespace-nowrap"
                        >
                            Start a Conversation <ArrowUpRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="py-24 lg:py-32 bg-[#080E20] border-t border-white/5" data-testid="services-process">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="mb-16">
                        <p className="overline">Our process</p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight max-w-2xl">
                            From first message to <em className="italic text-gold">safe return.</em>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            { step: "01", title: "Discovery call", text: "We learn your travel style, preferences, budget and dates. No forms — just a conversation." },
                            { step: "02", title: "Itinerary design", text: "Within 48 hours, your concierge sends a hand-crafted itinerary with curated options at each stage." },
                            { step: "03", title: "Refinement", text: "We iterate together until every detail feels right. Hotels, guides, pacing — all adjusted to your taste." },
                            { step: "04", title: "Booking & logistics", text: "We handle every booking, transfer, visa and insurance. You receive a clean travel pack before departure." },
                            { step: "05", title: "On-trip support", text: "Our concierge is reachable 24/7 throughout your journey. Missed flight? We're already rerouting you." },
                            { step: "06", title: "Post-trip debrief", text: "We follow up after every trip to learn what worked and what could be better. Your feedback shapes the next journey." },
                        ].map((item, i) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: i * 0.06 }}
                                className="flex gap-5"
                            >
                                <p className="font-serif text-4xl text-gold/20 leading-none shrink-0">{item.step}</p>
                                <div>
                                    <h3 className="font-serif text-xl">{item.title}</h3>
                                    <p className="font-sans text-sm text-white/60 mt-2 leading-relaxed">{item.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 lg:py-32 bg-navy" data-testid="services-faq">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <p className="overline">FAQ</p>
                        <h2 className="font-serif text-3xl sm:text-4xl font-medium mt-4 leading-tight">
                            Questions we <em className="italic text-gold">hear often.</em>
                        </h2>
                        <p className="font-sans text-sm text-white/60 mt-5 leading-relaxed">
                            Don't see your question? Just ask — we reply within a few hours.
                        </p>
                        <a
                            href={waLink("Hi! I have a question about your services.")}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                        >
                            Ask us directly <ArrowUpRight size={12} weight="bold" />
                        </a>
                    </div>
                    <div className="lg:col-span-8">
                        {FAQS.map((item, i) => (
                            <FAQItem
                                key={i}
                                item={item}
                                isOpen={openFaq === i}
                                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}
